import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Logo from '../components/Logo';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft,
  BarChart3,
  TrendingUp,
  TrendingDown,
  MapPin,
  Search,
  Heart,
  Building2,
  Euro,
  Target,
  Calendar,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
  Sparkles,
  Bot,
  Home,
  PieChart,
  LineChart,
  Info,
  Crown
} from 'lucide-react';

const AIStatsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [priceEvolution, setPriceEvolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceLoading, setPriceLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const isClubMember = hasFullAccess(user?.subscription_tier);

  useEffect(() => {
    if (isClubMember) {
      fetchStats();
      fetchPriceEvolution();
    } else {
      setLoading(false);
    }
  }, [isClubMember]);

  // Show upgrade screen for non-club members
  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={BarChart3}
        iconBgClass="from-indigo-500 to-purple-600"
        iconColorClass="text-indigo-500"
        buttonGradientClass="from-indigo-500 to-purple-600"
        title="Statistiques IA"
        currentTier={user?.subscription_tier}
        features={[
          { icon: TrendingUp, title: "Évolution des prix", description: "Suivez les tendances du marché dans vos zones de recherche" },
          { icon: Target, title: "Score de matching", description: "Analysez la correspondance des biens avec vos critères" },
          { icon: PieChart, title: "Analyses détaillées", description: "Visualisez vos recherches par type, localisation et budget" }
        ]}
      />
    );
  }

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 403) {
        toast.error('Cette fonctionnalité est réservée aux abonnés Club');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceEvolution = async () => {
    try {
      const response = await axios.get(`${API}/ai-agent/stats/price-evolution`, { withCredentials: true });
      setPriceEvolution(response.data);
      // Auto-select first location
      if (response.data?.locations) {
        const firstLocation = Object.keys(response.data.locations)[0];
        setSelectedLocation(firstLocation);
      }
    } catch (error) {
      console.error('Error fetching price evolution:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const formatPriceM2 = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(price) + ' €/m²';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#0D47A1] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Logo size="default" linkTo="/dashboard" />
              <Link to="/ai-agent" className="text-slate-600 hover:text-slate-900 flex items-center text-sm">
                <ChevronLeft className="w-4 h-4 mr-1" /> Retour à l'Agent IA
              </Link>
            </div>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Bot className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucune donnée disponible</h2>
          <p className="text-slate-600 mb-6">Effectuez votre première recherche avec l'Agent IA pour voir vos statistiques.</p>
          <Link to="/ai-agent">
            <Button className="bg-[#0D47A1]">
              <Search className="w-4 h-4 mr-2" /> Lancer une recherche
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { overview, location_stats, price_analysis, type_breakdown, daily_activity, top_matches, insights } = stats;

  // Prepare chart data for daily activity
  const activityDays = Object.keys(daily_activity).sort();
  const maxActivity = Math.max(...Object.values(daily_activity), 1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Logo size="default" linkTo="/dashboard" />
              <div className="hidden md:flex items-center space-x-2 text-slate-400">
                <span>/</span>
                <Link to="/ai-agent" className="text-slate-600 hover:text-[#0D47A1]">Agent IA</Link>
                <span>/</span>
                <span className="text-slate-900 font-medium">Statistiques</span>
              </div>
            </div>
            <Link to="/ai-agent">
              <Button variant="outline" size="sm">
                <Search className="w-4 h-4 mr-2" /> Nouvelle recherche
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Mes Statistiques IA</h1>
              <p className="text-purple-100">Analyse de vos recherches immobilières</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Search className="w-6 h-6 text-[#0D47A1] mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{overview.total_searches}</p>
              <p className="text-xs text-slate-500">Recherches</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{overview.total_listings_found}</p>
              <p className="text-xs text-slate-500">Biens trouvés</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Heart className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{overview.saved_listings}</p>
              <p className="text-xs text-slate-500">Sauvegardés</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{overview.searches_last_30_days}</p>
              <p className="text-xs text-slate-500">30 derniers jours</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Euro className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{formatPrice(overview.avg_budget).replace('€', '')}</p>
              <p className="text-xs text-slate-500">Budget moyen</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-[#00B050] mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{overview.avg_match_score}%</p>
              <p className="text-xs text-slate-500">Score moyen</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-gradient-to-br from-[#00B050]/10 to-emerald-100">
            <CardContent className="p-4 text-center">
              <Sparkles className="w-6 h-6 text-[#00B050] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#00B050]">{overview.high_match_listings}</p>
              <p className="text-xs text-slate-600">Top matches (70%+)</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Activity Chart */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <TrendingUp className="w-5 h-5 mr-2 text-[#0D47A1]" />
                Activité des 7 derniers jours
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activityDays.length > 0 ? (
                <div className="flex items-end justify-between h-32 gap-2">
                  {activityDays.map(day => {
                    const count = daily_activity[day] || 0;
                    const height = (count / maxActivity) * 100;
                    const dayName = new Date(day).toLocaleDateString('fr-FR', { weekday: 'short' });
                    return (
                      <div key={day} className="flex-1 flex flex-col items-center">
                        <span className="text-xs text-slate-600 mb-1">{count}</span>
                        <div 
                          className="w-full bg-gradient-to-t from-[#0D47A1] to-purple-500 rounded-t-md transition-all"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-xs text-slate-500 mt-2">{dayName}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center text-slate-400">
                  Pas d'activité cette semaine
                </div>
              )}
            </CardContent>
          </Card>

          {/* Insights */}
          <Card className="border-slate-200 bg-gradient-to-br from-purple-50 to-indigo-50">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                Vos tendances
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {insights.most_searched_location && (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-[#0D47A1] mr-2" />
                    <span className="text-sm text-slate-600">Zone favorite</span>
                  </div>
                  <span className="font-semibold text-slate-900">{insights.most_searched_location}</span>
                </div>
              )}
              {insights.most_common_type && (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <Home className="w-4 h-4 text-purple-600 mr-2" />
                    <span className="text-sm text-slate-600">Type préféré</span>
                  </div>
                  <span className="font-semibold text-slate-900 capitalize">{insights.most_common_type}</span>
                </div>
              )}
              {overview.avg_budget > 0 && (
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <Euro className="w-4 h-4 text-amber-600 mr-2" />
                    <span className="text-sm text-slate-600">Budget cible</span>
                  </div>
                  <span className="font-semibold text-slate-900">{formatPrice(overview.avg_budget)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Price Evolution Chart */}
        <Card className="border-slate-200 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <LineChart className="w-5 h-5 mr-2 text-emerald-600" />
              Évolution des prix au m²
            </CardTitle>
            <CardDescription>
              Tendances sur 6 mois dans vos zones de recherche
            </CardDescription>
          </CardHeader>
          <CardContent>
            {priceLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-[#0D47A1] animate-spin" />
              </div>
            ) : priceEvolution?.locations ? (
              <div>
                {/* Location Selector */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {Object.entries(priceEvolution.locations).map(([loc, data]) => (
                    <button
                      key={loc}
                      onClick={() => setSelectedLocation(loc)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                        selectedLocation === loc
                          ? 'bg-[#0D47A1] text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`location-btn-${loc}`}
                    >
                      <MapPin className="w-3 h-3" />
                      {loc}
                      {data.trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-400" />}
                      {data.trend === 'down' && <TrendingDown className="w-3 h-3 text-red-400" />}
                    </button>
                  ))}
                </div>

                {/* Selected Location Stats */}
                {selectedLocation && priceEvolution.locations[selectedLocation] && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Prix actuel</p>
                      <p className="text-xl font-bold text-[#0D47A1]">
                        {formatPriceM2(priceEvolution.locations[selectedLocation].current_price)}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Évolution 6 mois</p>
                      <p className={`text-xl font-bold flex items-center justify-center gap-1 ${
                        priceEvolution.locations[selectedLocation].change_pct > 0 ? 'text-emerald-600' :
                        priceEvolution.locations[selectedLocation].change_pct < 0 ? 'text-red-600' : 'text-slate-600'
                      }`}>
                        {priceEvolution.locations[selectedLocation].change_pct > 0 && <ArrowUp className="w-4 h-4" />}
                        {priceEvolution.locations[selectedLocation].change_pct < 0 && <ArrowDown className="w-4 h-4" />}
                        {priceEvolution.locations[selectedLocation].change_pct > 0 ? '+' : ''}
                        {priceEvolution.locations[selectedLocation].change_pct}%
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Variation</p>
                      <p className={`text-xl font-bold ${
                        priceEvolution.locations[selectedLocation].change_6m > 0 ? 'text-emerald-600' :
                        priceEvolution.locations[selectedLocation].change_6m < 0 ? 'text-red-600' : 'text-slate-600'
                      }`}>
                        {priceEvolution.locations[selectedLocation].change_6m > 0 ? '+' : ''}
                        {priceEvolution.locations[selectedLocation].change_6m} €/m²
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-sm text-slate-500">Vos recherches</p>
                      <p className="text-xl font-bold text-purple-600">
                        {priceEvolution.locations[selectedLocation].search_count}
                      </p>
                    </div>
                  </div>
                )}

                {/* Line Chart */}
                {selectedLocation && priceEvolution.locations[selectedLocation] && (
                  <div className="relative h-64 bg-gradient-to-b from-slate-50 to-white rounded-lg p-4">
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-4 bottom-12 w-16 flex flex-col justify-between text-xs text-slate-500">
                      {(() => {
                        const data = priceEvolution.locations[selectedLocation].data;
                        const prices = data.map(d => d.price_m2);
                        const maxPrice = Math.max(...prices);
                        const minPrice = Math.min(...prices);
                        const range = maxPrice - minPrice || 100;
                        return (
                          <>
                            <span>{Math.round(maxPrice + range * 0.1).toLocaleString('fr-FR')}</span>
                            <span>{Math.round((maxPrice + minPrice) / 2).toLocaleString('fr-FR')}</span>
                            <span>{Math.round(minPrice - range * 0.1).toLocaleString('fr-FR')}</span>
                          </>
                        );
                      })()}
                    </div>

                    {/* Chart area */}
                    <div className="ml-16 h-full">
                      <svg viewBox="0 0 500 200" className="w-full h-full" preserveAspectRatio="none">
                        {/* Grid lines */}
                        <line x1="0" y1="50" x2="500" y2="50" stroke="#e2e8f0" strokeWidth="1" />
                        <line x1="0" y1="100" x2="500" y2="100" stroke="#e2e8f0" strokeWidth="1" />
                        <line x1="0" y1="150" x2="500" y2="150" stroke="#e2e8f0" strokeWidth="1" />

                        {/* Line chart */}
                        {(() => {
                          const data = priceEvolution.locations[selectedLocation].data;
                          const prices = data.map(d => d.price_m2);
                          const maxPrice = Math.max(...prices);
                          const minPrice = Math.min(...prices);
                          const range = maxPrice - minPrice || 100;
                          const padding = range * 0.1;
                          
                          const points = data.map((d, i) => {
                            const x = (i / (data.length - 1)) * 480 + 10;
                            const y = 180 - ((d.price_m2 - minPrice + padding) / (range + padding * 2)) * 160;
                            return `${x},${y}`;
                          }).join(' ');

                          // Area fill gradient
                          const areaPoints = `10,180 ${points} ${(data.length - 1) / (data.length - 1) * 480 + 10},180`;

                          return (
                            <>
                              {/* Gradient fill */}
                              <defs>
                                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#0D47A1" stopOpacity="0.3" />
                                  <stop offset="100%" stopColor="#0D47A1" stopOpacity="0.05" />
                                </linearGradient>
                              </defs>
                              <polygon points={areaPoints} fill="url(#priceGradient)" />
                              
                              {/* Line */}
                              <polyline
                                points={points}
                                fill="none"
                                stroke="#0D47A1"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />

                              {/* Data points */}
                              {data.map((d, i) => {
                                const x = (i / (data.length - 1)) * 480 + 10;
                                const y = 180 - ((d.price_m2 - minPrice + padding) / (range + padding * 2)) * 160;
                                return (
                                  <g key={i}>
                                    <circle
                                      cx={x}
                                      cy={y}
                                      r="6"
                                      fill={d.is_actual ? "#00B050" : "#0D47A1"}
                                      stroke="white"
                                      strokeWidth="2"
                                    />
                                  </g>
                                );
                              })}
                            </>
                          );
                        })()}
                      </svg>

                      {/* X-axis labels */}
                      <div className="flex justify-between mt-2 text-xs text-slate-500">
                        {priceEvolution.locations[selectedLocation].data.map((d, i) => (
                          <span key={i}>
                            {new Date(d.month + '-01').toLocaleDateString('fr-FR', { month: 'short' })}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#0D47A1]" />
                    <span>Prix estimé du marché</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#00B050]" />
                    <span>Prix réel de vos recherches</span>
                  </div>
                </div>

                {/* Data source */}
                <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
                  <Info className="w-3 h-3" />
                  {priceEvolution.data_source}
                </p>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-slate-400">
                <LineChart className="w-12 h-12 mb-3 opacity-50" />
                <p>Aucune donnée d'évolution disponible</p>
                <p className="text-sm mt-1">Effectuez des recherches pour voir les tendances</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Locations Analysis */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <MapPin className="w-5 h-5 mr-2 text-emerald-600" />
                Analyse par localisation
              </CardTitle>
              <CardDescription>Vos recherches par zone géographique</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(location_stats).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(location_stats)
                    .sort((a, b) => b[1].searches - a[1].searches)
                    .slice(0, 5)
                    .map(([loc, data]) => {
                      const priceData = price_analysis[loc];
                      return (
                        <div key={loc} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900">{loc}</span>
                            <span className="text-sm text-slate-500">{data.searches} recherche(s)</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">{data.total_listings} biens analysés</span>
                            {priceData && (
                              <span className="text-[#0D47A1] font-medium">
                                Moy: {formatPrice(priceData.avg)}
                              </span>
                            )}
                          </div>
                          {priceData && (
                            <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                              <span>Min: {formatPrice(priceData.min)}</span>
                              <span>Max: {formatPrice(priceData.max)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  Aucune donnée de localisation
                </div>
              )}
            </CardContent>
          </Card>

          {/* Type Breakdown */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <PieChart className="w-5 h-5 mr-2 text-purple-600" />
                Types de biens recherchés
              </CardTitle>
              <CardDescription>Répartition par catégorie</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(type_breakdown).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(type_breakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count], idx) => {
                      const total = Object.values(type_breakdown).reduce((a, b) => a + b, 0);
                      const percentage = Math.round((count / total) * 100);
                      const colors = ['bg-[#0D47A1]', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
                      return (
                        <div key={type}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700 capitalize">{type}</span>
                            <span className="text-sm text-slate-500">{count} ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${colors[idx % colors.length]}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  Aucune donnée de type
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Matches */}
        {top_matches && top_matches.length > 0 && (
          <Card className="border-slate-200 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Target className="w-5 h-5 mr-2 text-[#00B050]" />
                Vos meilleures correspondances
              </CardTitle>
              <CardDescription>Les biens avec le meilleur score de matching</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {top_matches.map((listing, idx) => (
                  <a 
                    key={idx}
                    href={listing.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-4 bg-gradient-to-br from-slate-50 to-emerald-50 rounded-lg border border-emerald-100 block transition-all ${
                      listing.url ? 'hover:shadow-lg hover:border-[#00B050] cursor-pointer' : 'cursor-default'
                    }`}
                    onClick={(e) => !listing.url && e.preventDefault()}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        listing.match_score >= 80 ? 'bg-[#00B050] text-white' :
                        listing.match_score >= 60 ? 'bg-emerald-100 text-emerald-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {listing.match_score}%
                      </span>
                      {listing.estimated_yield && (
                        <span className="text-xs text-[#00B050] font-medium">
                          {listing.estimated_yield.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-slate-900 text-sm line-clamp-2 mb-1">
                      {listing.title}
                    </h4>
                    <p className="text-lg font-bold text-[#0D47A1]">
                      {formatPrice(listing.price)}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-500 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {listing.localisation}
                      </p>
                      {listing.surface && (
                        <p className="text-xs text-slate-500">{listing.surface} m²</p>
                      )}
                    </div>
                    {listing.url && (
                      <p className="text-xs text-[#0D47A1] mt-2 flex items-center">
                        Voir l'annonce →
                      </p>
                    )}
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="text-center py-8">
          <p className="text-slate-600 mb-4">Continuez à affiner vos critères pour de meilleurs résultats</p>
          <Link to="/ai-agent">
            <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
              <Bot className="w-4 h-4 mr-2" />
              Lancer une nouvelle recherche IA
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default AIStatsPage;
